/**
 * Thin DOM HUD for the Last Touch game.
 *
 * Game state (pot, king, time left, leaderboard, rules, chat) lives on
 * wall-mounted diegetic plaques in the throne hall — see KingNameplate,
 * TokenSign, RoundTimerPlaque, LeaderboardPlaque, RulesBoard, ChatMirrorBoard.
 * Only affordances stay as DOM:
 *
 *   - Bottom center: TOUCH / END / START action button.
 *   - Tx feedback row (success sig + error).
 *   - Connect-wallet hint when no wallet attached.
 */
import { BN } from "@coral-xyz/anchor";
import React, { useEffect, useState } from "react";

import type { KothLobbyController } from "./useKothLobbyController";
import type { KothRoundState } from "./useKothRound";
import { DraggableOverlay } from "../../util/DraggableOverlay";
import { useIsTouch } from "../../util/useIsTouch";
import { useStore } from "../../store";

const KOTH_BASE_FEE_LAMPORTS = 1_000_000n;
const KOTH_STEP_FEE_LAMPORTS = 100_000n;
const KOTH_KEEPER_BOUNTY_LAMPORTS = 500_000n;
const LAMPORTS_PER_SOL = 1_000_000_000n;

interface KothHudProps {
  controller: KothLobbyController;
}

export function KothHud({ controller }: KothHudProps): JSX.Element {
  const {
    round,
    roundId,
    walletConnected,
    expired,
    settled,
    txInFlight,
    txError,
    lastSig,
    pollError,
    startRound,
    touch,
    endRound,
    sessionKeyExpiresAt,
    enableSessionKey,
    disableSessionKey,
    isYouKing,
    seasonClosedAt,
    staleClaimWindowSecs,
  } = controller;
  const isTouch = useIsTouch();
  const isSpectator = useStore((s) => s.localPlayer?.isSpectator === true);

  const needsStart = !roundId || !round || settled;

  // Spectators see the same plaques but get no write affordances.
  if (isSpectator) {
    return <SpectatorPill />;
  }

  return (
    <>
      <ActionRow
        walletConnected={walletConnected}
        needsStart={needsStart}
        expired={expired}
        settled={settled}
        round={round}
        txInFlight={txInFlight}
        isTouch={isTouch}
        isYouKing={isYouKing}
        onStart={startRound}
        onTouch={touch}
        onEnd={endRound}
      />
      <SessionKeyBadge
        walletConnected={walletConnected}
        expiresAt={sessionKeyExpiresAt}
        txInFlight={txInFlight}
        onEnable={enableSessionKey}
        onDisable={disableSessionKey}
        isTouch={isTouch}
      />
      <ClaimDeadlineBadge
        walletConnected={walletConnected}
        closedAt={seasonClosedAt}
        windowSecs={staleClaimWindowSecs}
      />
      <TxFeedback
        isTouch={isTouch}
        txError={txError}
        lastSig={lastSig}
        pollError={pollError}
      />
    </>
  );
}

interface SessionKeyBadgeProps {
  walletConnected: boolean;
  expiresAt: number | null;
  txInFlight: boolean;
  onEnable: () => void;
  onDisable: () => void;
  isTouch: boolean;
}
function SessionKeyBadge({
  walletConnected,
  expiresAt,
  txInFlight,
  onEnable,
  onDisable,
  isTouch,
}: SessionKeyBadgeProps): JSX.Element {
  if (!walletConnected) return <></>;
  const nowSec = Math.floor(Date.now() / 1000);
  const active = expiresAt !== null && expiresAt > nowSec;
  const remaining = active ? Math.max(0, expiresAt - nowSec) : 0;
  return (
    <div
      style={
        isTouch
          ? { ...sessionBadgeStyle, transform: "translateX(-50%) scale(0.78)" }
          : sessionBadgeStyle
      }
    >
      {active ? (
        <>
          <span style={{ color: "#7fe19a" }}>
            ⚡ auto-approve on · {Math.ceil(remaining / 60)}m left
          </span>
          <button
            onClick={onDisable}
            disabled={txInFlight}
            style={sessionBadgeBtnStyle}
          >
            disable
          </button>
        </>
      ) : (
        <button
          onClick={onEnable}
          disabled={txInFlight}
          style={sessionBadgeBtnStyle}
          title="Authorize an ephemeral key for 15 minutes of popup-free touches."
        >
          ⚡ enable auto-approve (15 min)
        </button>
      )}
    </div>
  );
}

interface ClaimDeadlineBadgeProps {
  walletConnected: boolean;
  closedAt: number | null;
  windowSecs: number | null;
}
function ClaimDeadlineBadge({
  walletConnected,
  closedAt,
  windowSecs,
}: ClaimDeadlineBadgeProps): JSX.Element {
  // TODO: render countdown once season.closed_at + stale_claim_window_secs
  // land in the IDL. Until then the controller returns null and we hide the
  // badge entirely rather than show "expires in NaN" or a placeholder.
  const [nowSec, setNowSec] = useState<number>(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (!walletConnected || !closedAt || !windowSecs) return;
    const id = setInterval(() => {
      setNowSec(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [walletConnected, closedAt, windowSecs]);
  if (!walletConnected || !closedAt || !windowSecs) return <></>;
  const deadline = closedAt + windowSecs;
  const remaining = deadline - nowSec;
  const expired = remaining <= 0;
  const label = expired ? "expired" : `expires in ${formatRemaining(remaining)}`;
  return (
    <div style={claimDeadlineStyle}>
      <span style={{ color: expired ? "#f4a4b4" : "#f4d36a" }}>claim {label}</span>
    </div>
  );
}

function formatRemaining(secs: number): string {
  const days = Math.floor(secs / 86_400);
  const hours = Math.floor((secs % 86_400) / 3_600);
  const mins = Math.floor((secs % 3_600) / 60);
  if (secs >= 86_400) return `${days}d ${hours}h`;
  if (secs >= 3_600) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

interface ActionRowProps {
  walletConnected: boolean;
  needsStart: boolean;
  expired: boolean;
  settled: boolean;
  round: KothRoundState | null;
  txInFlight: boolean;
  isTouch: boolean;
  isYouKing: boolean;
  onStart: () => void;
  onTouch: () => void;
  onEnd: () => void;
}
function ActionRow({
  walletConnected,
  needsStart,
  expired,
  settled,
  round,
  txInFlight,
  isTouch,
  isYouKing,
  onStart,
  onTouch,
  onEnd,
}: ActionRowProps): JSX.Element {
  // Touch: pinned to top-center (above the joystick); draggable.
  // Desktop: bottom-center, original layout, no drag.
  const inner = (() => {
    if (!walletConnected) {
      return (
        <div style={isTouch ? hintTouchStyle : hintStyle}>
          {isTouch ? "Connect a wallet to play Last Touch" : "Connect a wallet (top-right) to play Last Touch"}
        </div>
      );
    }
    if (needsStart) {
      return (
        <PrimaryButton onClick={onStart} disabled={txInFlight} isTouch={isTouch}>
          {txInFlight ? "Submitting…" : "Start round (10 min)"}
        </PrimaryButton>
      );
    }
    if (expired && round && !settled) {
      return (
        <PrimaryButton onClick={onEnd} disabled={txInFlight} isTouch={isTouch}>
          {txInFlight
            ? "Submitting…"
            : `End round (+${lamportsToSol(new BN(KOTH_KEEPER_BOUNTY_LAMPORTS.toString()))} SOL bounty)`}
        </PrimaryButton>
      );
    }
    if (round) {
      if (isYouKing) {
        // Self-touch is blocked on-chain (LastTouchError::TouchKingSelfTouch).
        // Render a passive label instead of a dead-disabled button so the king
        // gets a clear "wait for a challenger" cue.
        return (
          <div style={isTouch ? hintTouchStyle : hintStyle}>
            👑 you hold the throne — wait for a challenger
          </div>
        );
      }
      const fee = nextFeeLamports(round);
      const vigPct = readVigPct(round);
      const houseSuffix = vigPct > 0 ? ` (incl. ${vigPct}% house)` : "";
      return (
        <PrimaryButton onClick={onTouch} disabled={txInFlight} isTouch={isTouch}>
          {txInFlight
            ? "Submitting…"
            : `${isTouch ? "" : "[F] "}TOUCH THE THRONE (${lamportsToSolBigint(fee)} SOL)${houseSuffix}`}
        </PrimaryButton>
      );
    }
    return null;
  })();

  if (inner == null) return <></>;

  if (isTouch) {
    return (
      <DraggableOverlay
        id="koth-action-row"
        // Bottom-left, right next to the EcctrlJoystick (~200px wide, sits at
        // the screen's bottom-left corner). Keeps the action button within
        // right-thumb reach while the left thumb stays on the joystick.
        defaultAnchor={{ left: 220, bottom: 24, safeAreaBottom: true }}
        enabled
        zIndex={5}
        nonDragSelector="button"
      >
        <div style={{ display: "flex", gap: 10 }}>{inner}</div>
      </DraggableOverlay>
    );
  }
  return <div style={actionRowStyle}>{inner}</div>;
}

/**
 * Squash giant Solana simulation dumps into a single readable line. The full
 * text is preserved on the wrapper element's `title` so power users can still
 * hover to see everything. Keep the patterns ordered most-specific-first.
 *
 * Defined above TxFeedback (its only caller) so Vite's React-Refresh HMR
 * boundary captures both in the same module update — otherwise a hot-replaced
 * TxFeedback can race ahead of this symbol and throw `ReferenceError`.
 */
function friendlyTxError(raw: string): string {
  const msg = raw ?? "";
  const anchorMsg = msg.match(/Error Message:\s*([^.\n]+)\.?/);
  const anchorCode = msg.match(/Error Code:\s*([A-Za-z0-9_]+)/);
  const anchorMsgText = anchorMsg?.[1]?.trim();
  if (anchorMsgText) {
    if (/KothRoundNotOpen/i.test(msg)) return "round is over — click End round";
    if (/KothCooldown/i.test(msg)) return "cooldown — wait a moment before re-touching";
    if (/MaxTouchesPerWallet/i.test(msg)) return "you've hit the per-wallet touch cap for this round";
    if (/TouchKingSelfTouch/i.test(msg)) return "you're already king — wait for a challenger";
    if (/SessionKeyExpired/i.test(msg)) return "rapid-fire key expired — re-enable below";
    if (/SessionKeyMismatch/i.test(msg)) return "rapid-fire key revoked — re-enable below";
    return anchorMsgText;
  }
  if (/insufficient funds for rent/i.test(msg)) {
    return "rapid-fire key is low on funds — disable & re-enable to top up";
  }
  if (/insufficient lamports/i.test(msg)) {
    return "wallet balance too low to cover the touch fee";
  }
  if (/blockhash not found|TransactionExpired/i.test(msg)) {
    return "tx timed out — try again";
  }
  if (/User rejected|wallet (closed|rejected)/i.test(msg)) {
    return "wallet popup rejected";
  }
  if (anchorCode?.[1]) return anchorCode[1];
  return (msg.split("\n")[0] ?? "").slice(0, 140);
}

interface TxFeedbackProps {
  isTouch: boolean;
  txError: string | null;
  lastSig: string | null;
  pollError: string | null;
}
function TxFeedback({ isTouch, txError, lastSig, pollError }: TxFeedbackProps): JSX.Element {
  if (!txError && !lastSig && !pollError) return <></>;
  const friendly = txError ? friendlyTxError(txError) : null;
  const body = (
    <>
      {pollError && <span style={{ color: "#f4a4b4" }}>poll: {pollError}</span>}
      {friendly && (
        <span style={{ color: "#f4a4b4" }} title={txError ?? undefined}>
          {friendly}
        </span>
      )}
      {lastSig && !txError && (
        <span style={{ color: "#9aa0b4", fontFamily: "monospace" }}>
          sig: {lastSig.slice(0, 12)}…
        </span>
      )}
    </>
  );

  if (isTouch) {
    return (
      <DraggableOverlay
        id="koth-tx-feedback"
        // Sits directly above the touch action button so confirmation /
        // error text appears next to the thing the player just tapped.
        defaultAnchor={{ left: 220, bottom: 90, safeAreaBottom: true }}
        enabled
        zIndex={5}
      >
        <div style={feedbackStripBodyStyle}>{body}</div>
      </DraggableOverlay>
    );
  }
  return <div style={feedbackStripStyle}>{body}</div>;
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  isTouch,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isTouch?: boolean;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        // Apple HIG / Material both recommend a minimum 44px tap target. Bump
        // padding and font size on touch; desktop stays compact.
        padding: isTouch ? "16px 24px" : "10px 18px",
        minHeight: isTouch ? 52 : undefined,
        background: disabled ? "#2a2a36" : "#4a8eff",
        color: "#fff",
        border: "none",
        borderRadius: 8,
        fontSize: isTouch ? 16 : 14,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1,
        pointerEvents: "auto",
        touchAction: "manipulation",
      }}
    >
      {children}
    </button>
  );
}

function SpectatorPill(): JSX.Element {
  return (
    <div
      style={{
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        padding: "4px 10px",
        background: "rgba(20,20,30,0.85)",
        border: "1px solid #2a2a36",
        borderRadius: 999,
        color: "#9aa0b4",
        fontSize: 11,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        zIndex: 5,
        pointerEvents: "none",
      }}
    >
      Spectating
    </div>
  );
}

function lamportsToSol(lamports: BN): string {
  return lamportsToSolBigint(BigInt(lamports.toString()));
}
function lamportsToSolBigint(lamports: bigint): string {
  const whole = lamports / LAMPORTS_PER_SOL;
  const frac = lamports % LAMPORTS_PER_SOL;
  return `${whole}.${frac.toString().padStart(9, "0").slice(0, 6)}`;
}

function nextFeeLamports(round: KothRoundState): bigint {
  const snap = round.paramsSnapshot;
  const base = snap?.baseFeeLamports
    ? BigInt(snap.baseFeeLamports.toString())
    : KOTH_BASE_FEE_LAMPORTS;
  const step = snap?.stepFeeLamports
    ? BigInt(snap.stepFeeLamports.toString())
    : KOTH_STEP_FEE_LAMPORTS;
  const staticFee = base + step * BigInt(round.touchCount);
  const dynBps = snap?.dynamicFeeBps;
  if (!dynBps || dynBps <= 0) return staticFee;
  const pot = BigInt(round.potLamports.toString());
  const dynamicFloor = (pot * BigInt(dynBps)) / 10_000n;
  return staticFee > dynamicFloor ? staticFee : dynamicFloor;
}

function readVigPct(round: KothRoundState): number {
  // TODO: vig_bps from on-chain params once IDL ships — until then the field
  // is absent from paramsSnapshot, so we sentinel to 0 and suppress the suffix.
  const snap = round.paramsSnapshot as unknown as { vigBps?: number } | undefined;
  const bps = snap?.vigBps;
  if (typeof bps !== "number" || bps <= 0) return 0;
  return bps / 100;
}

const sessionBadgeStyle: React.CSSProperties = {
  position: "absolute",
  // Stacked above the TOUCH/END action button + tx feedback strip so it's
  // impossible to miss. Top-right is taken by the WalletButton + voice HUDs,
  // which was hiding the badge entirely.
  bottom: 110,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 8,
  alignItems: "center",
  padding: "6px 12px",
  background: "rgba(20,20,30,0.9)",
  border: "1px solid #5a4a18",
  borderRadius: 999,
  fontSize: 12,
  color: "#e8e8ef",
  zIndex: 5,
  pointerEvents: "auto",
  boxShadow: "0 0 0 1px rgba(255, 214, 107, 0.08)",
};

const sessionBadgeBtnStyle: React.CSSProperties = {
  padding: "4px 8px",
  background: "#2a2a36",
  color: "#e8e8ef",
  border: "1px solid #3a3a48",
  borderRadius: 6,
  fontSize: 11,
  cursor: "pointer",
};

const claimDeadlineStyle: React.CSSProperties = {
  position: "absolute",
  bottom: 146,
  left: "50%",
  transform: "translateX(-50%)",
  padding: "4px 10px",
  background: "rgba(20,20,30,0.9)",
  border: "1px solid #5a4a18",
  borderRadius: 999,
  fontSize: 11,
  letterSpacing: 0.3,
  color: "#e8e8ef",
  zIndex: 5,
  pointerEvents: "none",
  boxShadow: "0 0 0 1px rgba(255, 214, 107, 0.08)",
};

const actionRowStyle: React.CSSProperties = {
  position: "absolute",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 10,
  zIndex: 5,
  pointerEvents: "none",
};

const hintStyle: React.CSSProperties = {
  padding: "8px 14px",
  background: "rgba(20,20,30,0.85)",
  border: "1px solid #2a2a36",
  borderRadius: 8,
  color: "#9aa0b4",
  fontSize: 12,
  pointerEvents: "none",
};

const hintTouchStyle: React.CSSProperties = {
  padding: "12px 16px",
  background: "rgba(20,20,30,0.92)",
  border: "1px solid #2a2a36",
  borderRadius: 8,
  color: "#e8e8ef",
  fontSize: 14,
  pointerEvents: "none",
};

const feedbackStripStyle: React.CSSProperties = {
  position: "absolute",
  bottom: 70,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 12,
  fontSize: 11,
  padding: "4px 8px",
  background: "rgba(20,20,30,0.85)",
  border: "1px solid #2a2a36",
  borderRadius: 6,
  zIndex: 5,
  pointerEvents: "none",
  maxWidth: 600,
  wordBreak: "break-word",
};

// Touch-mode body style: positioning is handled by DraggableOverlay, so this
// only carries look & feel.
const feedbackStripBodyStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  fontSize: 12,
  padding: "6px 10px",
  background: "rgba(20,20,30,0.92)",
  border: "1px solid #2a2a36",
  borderRadius: 6,
  maxWidth: "90vw",
  wordBreak: "break-word",
};

